'use client';

import { usePageAction } from '@cc-shopping/platform-analytics/hooks/usePageAction';
import { CTASearchParams } from '@cc-shopping/platform-analytics/lib/ctaSearchParams';
import { FinishType } from '@cc-shopping/platform-analytics/types';
import { Box, type SxProps, type Theme } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';
import { PrimaryLink, SecondaryLink } from 'src/features/product-card/ApplyNowButton';
import { CtaLink } from 'src/features/product-card/CTA/CtaLink';
import { RatesAndFeesLink } from 'src/features/product-card/RatesAndFeesLink';
import CardImage from './components/CardImage';
import Rating from './components/Rating';
import type { MiniProductCardMarketplaceEntity } from './types';
import { useReviewSelfReferenceCheck } from './utils';

export type MiniProductCardProps = {
  align?: 'left' | 'right' | 'center' | 'none';
  marketplaceEntity: MiniProductCardMarketplaceEntity;
  useLargeWidth?: boolean;
  hideReviewWhenMonetizing?: boolean;
  ctaIcon?: ReactNode;
  ctaSx?: SxProps<Theme>;
};

export const MiniProductCard = forwardRef<HTMLDivElement, MiniProductCardProps>(
  (
    { align = 'center', marketplaceEntity, hideReviewWhenMonetizing = false, ctaIcon, ctaSx },
    ref,
  ) => {
    const fireReadReviewAction = usePageAction({
      actionName: 'read_review',
      actionSection: 'mini_product_card',
      actionType: 'select',
    });

    const reviewLinkIsSelfReferencing = useReviewSelfReferenceCheck(marketplaceEntity);

    if (!marketplaceEntity || !marketplaceEntity?.details?.isActive) {
      return null;
    }

    // The MiniProductCard has some float functionality it needs to maintain
    const rootContainerStyles = {
      marginBottom: '1.8rem',
      ...(align === 'left' && {
        display: 'block',
        float: 'left',
        maxWidth: '160px',
        marginRight: '1.8rem',
      }),
      ...(align === 'right' && {
        display: 'block',
        float: 'right',
        maxWidth: '160px',
        marginLeft: '1.8rem',
      }),
      ...(align === 'center' && {
        display: 'flex',
        justifyContent: 'center',
      }),
    };

    return (
      <Box ref={ref} id="mini-product-card-root" sx={rootContainerStyles}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              mb: 2,
              position: 'relative',
              width: 150,
              height: 94,
            }}
          >
            <CardImage
              marketplaceEntity={marketplaceEntity}
              source={marketplaceEntity.image.medium.url}
              blockImageClick={
                !marketplaceEntity?.details?.isMonetizing && reviewLinkIsSelfReferencing
              }
              href={
                marketplaceEntity?.details?.isMonetizing
                  ? marketplaceEntity.applyLink
                  : marketplaceEntity.reviewLink
              }
            />
          </Box>

          {marketplaceEntity.starRating && (
            <Box display="flex" justifyContent="center" mb={2}>
              <Rating
                marketplaceEntity={marketplaceEntity}
                blockCTAClick={reviewLinkIsSelfReferencing}
              />
            </Box>
          )}

          {/* APPLY NOW BUTTON */}
          {marketplaceEntity.details?.isMonetizing && marketplaceEntity.applyLink && (
            <Box width="100%" mb={2}>
              <CtaLink
                analytics={marketplaceEntity.ctaAnalytics}
                type="APPLY_NOW_BUTTON"
                href={marketplaceEntity.applyLink}
                Component={PrimaryLink}
                rel="nofollow"
                target="_blank"
                finishType={FinishType.ExternalApplication}
                parameterActions={[CTASearchParams.MONETIZING, CTASearchParams.SHOPPING_FINISH]}
                sx={ctaSx}
              >
                APPLY NOW
                {ctaIcon}
              </CtaLink>
            </Box>
          )}

          {/* REVIEW BUTTON */}
          {!reviewLinkIsSelfReferencing &&
            !(hideReviewWhenMonetizing && marketplaceEntity.details?.isMonetizing) && (
              <Box
                sx={{
                  width: '100%',
                  mb: 2,
                }}
              >
                <CtaLink
                  analytics={marketplaceEntity.ctaAnalytics}
                  type="APPLY_NOW_BUTTON"
                  href={marketplaceEntity.reviewLink}
                  Component={SecondaryLink}
                  finishType={FinishType.InternalContent}
                  parameterActions={[]}
                  skipShoppingFinishedEvent
                  onClickAnalyticsCallback={() => {
                    fireReadReviewAction();
                  }}
                >
                  READ REVIEW
                </CtaLink>
              </Box>
            )}

          {/* RATES AND FEES LINK */}
          {marketplaceEntity.details?.isMonetizing && marketplaceEntity.termsAndFeesLink && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <RatesAndFeesLink
                analytics={marketplaceEntity.ctaAnalytics}
                href={marketplaceEntity.termsAndFeesLink}
                versionId={marketplaceEntity.versionId}
                name={marketplaceEntity.displayName || marketplaceEntity.name}
              >
                Rates & Fees
              </RatesAndFeesLink>
            </Box>
          )}
        </Box>
      </Box>
    );
  },
);

export default MiniProductCard;
